import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  PhoneIcon,
  DevicePhoneMobileIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';
import { UserAvatar } from '../../components/ui';
import { headerCls } from '../../data/columns';
import { formatCurrency, formatPhoneNumber } from '../../utils/baseUtils';

/**
 * Sunucu tarafı sıralama durumuna bağlı, tıklanabilir kolon başlığı.
 * Aktif kolon yön okunu gösterir; diğer sıralanabilir kolonlar soluk
 * yukarı/aşağı ipucu gösterir.
 */
export const sortableTitle = (label, sortKey, sort, onSort) => {
  const isActive = sort.sortBy === sortKey;
  const Arrow = !isActive
    ? ChevronUpDownIcon
    : sort.sortOrder === 'asc'
      ? ChevronUpIcon
      : ChevronDownIcon;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="group inline-flex items-center gap-1"
    >
      <span className={headerCls}>{label}</span>
      <Arrow
        className={`h-3.5 w-3.5 transition ${
          isActive
            ? 'text-blue-600 dark:text-sky-400'
            : 'text-slate-300 group-hover:text-slate-400 dark:text-slate-600'
        }`}
        aria-hidden
      />
    </button>
  );
};

/**
 * Customers liste tablosunun kolonları.
 * @param sort   { sortBy, sortOrder }
 * @param onSort (key) => void
 */
export const buildCustomerColumns = ({ sort, onSort }) => [
  {
    title: sortableTitle('Name', 'name', sort, onSort),
    key: 'name',
    render: (_, customer) => {
      const hasName = !!customer.fullname;
      return (
        <div className="flex min-w-0 items-center gap-3">
          {hasName ? (
            <UserAvatar name={customer.fullname} size="md" />
          ) : (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-400">
              <PhoneIcon className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0">
            <p
              className={
                hasName
                  ? 'truncate text-sm font-semibold text-slate-900 dark:text-slate-100'
                  : 'text-sm text-slate-300 dark:text-slate-600'
              }
            >
              {customer.fullname || '—'}
            </p>
            {hasName && customer.email && (
              <p className="truncate text-xs text-slate-400 dark:text-slate-500">
                {customer.email}
              </p>
            )}
          </div>
        </div>
      );
    },
  },
  {
    title: <span className={headerCls}>Phone</span>,
    key: 'phone',
    render: (_, customer) =>
      customer.phone ? (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
          <span className="tabular-nums">
            {formatPhoneNumber(customer.phone)}
          </span>
          {customer.ismobile && (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-blue-50 px-1.5 py-0.5 text-[0.6rem] font-semibold text-blue-700 ring-1 ring-blue-100 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20">
              <DevicePhoneMobileIcon className="h-3 w-3" />
              App
            </span>
          )}
        </div>
      ) : (
        <span className="text-slate-300 dark:text-slate-600">—</span>
      ),
  },
  {
    title: sortableTitle('Last Order', 'lastorder', sort, onSort),
    key: 'lastorder',
    render: (_, customer) =>
      customer.lastorderdate ? (
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {moment(customer.lastorderdate).format('MMM Do')}
        </span>
      ) : (
        <span className="text-slate-300 dark:text-slate-600">—</span>
      ),
  },
  {
    title: sortableTitle('Life Time Spend', 'lifetimespend', sort, onSort),
    key: 'lifetimespend',
    align: 'right',
    render: (_, customer) => (
      <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {formatCurrency(customer.lifetimespend)}
      </span>
    ),
  },
  {
    title: sortableTitle('Orders', 'orders', sort, onSort),
    key: 'orders',
    align: 'right',
    render: (_, customer) =>
      customer.ordercount > 0 ? (
        <Link
          to={`${customer.id}/orders`}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-200/60 transition hover:bg-blue-100 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20 dark:hover:bg-sky-500/20"
        >
          <ShoppingBagIcon className="h-3.5 w-3.5" />
          {customer.ordercount} {customer.ordercount === 1 ? 'Order' : 'Orders'}
        </Link>
      ) : (
        <span className="text-sm text-slate-300 dark:text-slate-600">—</span>
      ),
  },
];
